import { canEngagePet, CardKind, Cell, ChallengeKind, Enemy, LevelPhase, LevelSim, OfferKind, SimConstants, SimEventKind } from "@highmarch/domain";
import { NEW_ENEMY_STEP_PREFIX } from "../ui/guide/scripts/levelHints";
import type { BusyReason, CardKindName, GuideWorld, LevelFacts, LevelPhaseName, OfferName, ScreenAnchor, WorldAnchor } from "../ui/guide/guide";

/**
 * The level's side of Captain Bram's guide (DESIGN §6.2–§8): the plain facts the scripts read, the world anchors the
 * overlay points at, the recommended tower cell and what keeps the guide quiet. Everything here only reads the sim.
 */

/** What the level UI knows that the sim does not (the chosen spot, the speed, the open sheets). */
export interface LevelUiFacts {
  readonly towerSpotChosen: boolean;
  readonly upgradeTargetChosen: boolean;
  readonly speed: number;
  readonly troopsFighting: boolean;
  readonly lastStand: boolean;
  readonly towerSheetOpen: boolean;
  readonly rallyArmed: boolean;
}

const PHASE_NAME: Readonly<Record<LevelPhase, LevelPhaseName>> = {
  [LevelPhase.OpeningDraft]: "draft", [LevelPhase.Ready]: "ready", [LevelPhase.WaveRunning]: "wave",
  [LevelPhase.BetweenWaves]: "break", [LevelPhase.Won]: "won", [LevelPhase.Lost]: "lost",
};
const OFFER_NAME: Readonly<Record<OfferKind, OfferName>> = {
  [OfferKind.OpeningPath]: "openingRoad", [OfferKind.OpeningTower]: "openingTower", [OfferKind.PostWave]: "inRun",
};
const CARD_NAME: Readonly<Record<CardKind, CardKindName>> = {
  [CardKind.Path]: "road", [CardKind.Tower]: "tower", [CardKind.Upgrade]: "upgrade", [CardKind.Ascend]: "ascend", [CardKind.Skill]: "skill", [CardKind.Move]: "move",
};

/** The level as the guide sees it: rebuilt after every sim change, never a live reference (scripts stay pure). */
export function levelFacts(sim: LevelSim, ui: LevelUiFacts): LevelFacts {
  const B = sim.balance, offer = sim.currentOffer;
  const picked = offer !== null && offer.hasPick ? offer.pickedCard : null;
  // M40: the Starfall Wyrm is its own moment (level/wyrmMoment.ts): no boss, flyer or new-raider hint stops the action for it
  // Pets v2: nor the chased wild pup (a boss to the sim only; its moments are level/levelPet.ts's)
  const alive = sim.enemySlots.filter((e) => e.alive && !B.enemies[e.archetype].legend && B.enemies[e.archetype].wildPet === "");
  return {
    level: sim.levelNumber,
    firstClear: sim.firstClear,
    phase: PHASE_NAME[sim.phase],
    stage: sim.stage,
    stages: sim.stages,
    waveSeconds: sim.phase === LevelPhase.WaveRunning ? Math.max(0, sim.tick - sim.stageStartTick) / SimConstants.TicksPerSecond : 0,
    offer: offer === null ? null : OFFER_NAME[offer.kind],
    offerCards: offer === null ? [] : offer.cards.map((c) => CARD_NAME[c.kind]),
    offerTowerIds: offer === null ? [] : offer.cards.filter((c) => c.kind === CardKind.Tower).map((c) => B.towers[c.index].id),
    picked: picked === null ? null : CARD_NAME[picked.kind],
    pickedTowerId: picked !== null && picked.kind === CardKind.Tower ? B.towers[picked.index].id : null,
    fits: picked !== null && picked.kind === CardKind.Path ? offer!.ghosts.length : 0,
    towerSpotChosen: ui.towerSpotChosen,
    upgradeTargetChosen: ui.upgradeTargetChosen,
    roadTiles: sim.road.designedTileCount,
    towersBuilt: sim.towerSlots.filter((t) => t.alive && !B.towers[t.archetype].isBase).length,
    rerollCharges: sim.rerollCharges,
    speed: ui.speed,
    points: sim.points,
    pointsToNextOffer: sim.pointsToNextOffer,
    bossAlive: alive.some((e) => e.isBoss),
    aliveEnemyIds: [...new Set(alive.map((e) => B.enemies[e.archetype].id))],
    airEnemyAlive: alive.some((e) => e.isAir),
    eliteAlive: alive.some((e) => e.affix >= 0),
    troopsFighting: ui.troopsFighting,
    lastStand: ui.lastStand,
    airTowerNames: B.towers.filter((a, i) => sim.ownedTowers[i] && a.targetsAir && !a.isBase).map((a) => a.name),
    noSkipChallenge: sim.level.challenge?.kind === ChallengeKind.NoSkip,
    towerSheetOpen: ui.towerSheetOpen,
    rallyArmed: ui.rallyArmed,
    wildPetOut: canEngagePet(sim),
  };
}

/** Where along the road (share of its length from the portal) a tie between equally good tower cells is broken. */
const RoadMidShare = 0.5;

/**
 * The legal cell for this tower card that covers the most road (DESIGN §8): ties go to the cell nearest the middle of
 * the road, then to the one nearest the fortress, then to grid order. Null when there is nowhere legal.
 */
export function recommendedTowerCell(sim: LevelSim, archetype: number): Cell | null {
  const cells = sim.collectValidTowerCells();
  if (cells.length === 0) return null;
  const base = sim.grid.base;
  const road = sim.road.roadCells.filter((c) => !c.equals(base));
  const r = sim.previewRange(archetype).toNumber(), r2 = r * r;
  const mid = road.length > 0 ? road[Math.floor(road.length * RoadMidShare)] : base;
  const d2 = (a: Cell, b: Cell) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
  let best: Cell | null = null, bestCover = -1, bestMid = Infinity, bestBase = Infinity;
  for (const c of cells) {
    let cover = 0;
    for (const rc of road) if (d2(c, rc) <= r2) cover++;
    const m = d2(c, mid), b = d2(c, base);
    if (cover > bestCover || (cover === bestCover && (m < bestMid || (m === bestMid && b < bestBase)))) {
      best = c; bestCover = cover; bestMid = m; bestBase = b;
    }
  }
  return best;
}

/** What the level knows about the screen for the guide's world anchors (LevelController supplies it). */
export interface GuideWorldSource {
  readonly sim: LevelSim;
  /** The road option shown for the picked road card. */
  ghostIndex(): number;
  towerSpot(): Cell | null;
  /** The guide step on screen: `newEnemy` reads the archetype a `hint.enemy.<id>` step names. */
  activeStep(): string | null;
  /** A cell's centre where it is drawn, in client CSS px (rig.cellOnScreen: the same projection taps use). */
  cellOnScreen(c: Cell): { readonly x: number; readonly y: number };
  /** An enemy's drawn body in client CSS px, or null when it has no view yet. */
  enemyOnScreen(id: number): { readonly x: number; readonly y: number } | null;
  /** The map band nothing covers: under the HUD, above the tray or the control row (client CSS px). */
  band(): { readonly top: number; readonly bottom: number };
  /** Ease the camera to a cell (rig.glideToCell; a jump when motion is reduced). */
  glideTo(c: Cell): void;
}

/** A focus leaves the camera alone when the anchor is at least this far inside the uncovered band (CSS px). */
const FocusMarginPx = 48;

/** The level's GuideWorld (DESIGN §8): world anchors → screen, every frame the overlay follows one. */
export class LevelGuideWorld implements GuideWorld {
  private recKey = "";
  private rec: Cell | null = null;

  constructor(private readonly src: GuideWorldSource) {}

  locate(anchor: WorldAnchor): ScreenAnchor | null {
    const at = this.resolve(anchor);
    if (at === null) return null;
    const p = at.enemy !== null ? this.src.enemyOnScreen(at.enemy) : this.src.cellOnScreen(at.cell);
    if (p === null) return null;
    const band = this.src.band();
    if (p.y < band.top || p.y > band.bottom) return null;
    return { x: p.x, y: p.y, r: this.cellPx(at.cell) };
  }

  focus(anchor: WorldAnchor): void {
    const at = this.resolve(anchor);
    if (at === null) return;
    const p = at.enemy !== null ? this.src.enemyOnScreen(at.enemy) : this.src.cellOnScreen(at.cell);
    const band = this.src.band();
    // inside the uncovered band vertically AND clear of the screen's sides (a cell at the edge was half off screen)
    const width = typeof window === "undefined" ? Infinity : window.innerWidth;
    const clear = p !== null && p.y >= band.top + FocusMarginPx && p.y <= band.bottom - FocusMarginPx && p.x >= FocusMarginPx && p.x <= width - FocusMarginPx;
    if (clear) return;
    this.src.glideTo(at.cell);
  }

  /** The anchor's cell (and the enemy drawn there, for units), or null when it does not exist now. */
  private resolve(anchor: WorldAnchor): { cell: Cell; enemy: number | null } | null {
    const sim = this.src.sim, cell = (c: Cell | null) => (c === null ? null : { cell: c, enemy: null });
    switch (anchor) {
      case "base": return cell(sim.grid.base);
      case "portal": return cell(sim.road.portal);
      case "towerSpot": return cell(this.src.towerSpot());
      case "recommendedTowerCell": return cell(this.recommended());
      case "ghost": {
        const offer = sim.currentOffer;
        if (offer === null || !offer.hasPick || offer.pickedCard.kind !== CardKind.Path || offer.ghosts.length === 0) return null;
        const g = offer.ghosts[Math.min(this.src.ghostIndex(), offer.ghosts.length - 1)].cells;
        return cell(g[g.length - 1] ?? null);
      }
      case "firstTower": {
        const t = sim.towerSlots.find((x) => x.alive && !sim.balance.towers[x.archetype].isBase);
        return cell(t ? t.cell : null);
      }
      case "boss": return unit(nearestToBase(sim, (e) => e.isBoss));
      case "firstFlyer": return unit(nearestToBase(sim, (e) => e.isAir && !sim.balance.enemies[e.archetype].legend));
      case "leadEnemy": return unit(nearestToBase(sim, () => true));
      case "elite": return unit(nearestToBase(sim, (e) => e.affix >= 0));
      case "newEnemy": {
        const id = this.src.activeStep();
        if (id === null || !id.startsWith(NEW_ENEMY_STEP_PREFIX)) return null;
        const arch = sim.balance.enemies.findIndex((a) => a.id === id.slice(NEW_ENEMY_STEP_PREFIX.length));
        return unit(nearestToBase(sim, (e) => e.archetype === arch));
      }
    }
  }

  /** The recommended cell for the tower card in hand, recomputed only when the offer, pick, road or towers change. */
  private recommended(): Cell | null {
    const sim = this.src.sim, offer = sim.currentOffer;
    if (offer === null || !offer.hasPick || offer.pickedCard.kind !== CardKind.Tower) return null;
    const key = `${offer.id}|${offer.pickedIndex}|${sim.road.designedTileCount}|${sim.towerSlots.filter((t) => t.alive).length}`;
    if (key !== this.recKey) { this.recKey = key; this.rec = recommendedTowerCell(sim, offer.pickedCard.index); }
    return this.rec;
  }

  /** About a cell on screen here: the drawn distance to its farther neighbour (the same measure as a tap's reach). */
  private cellPx(c: Cell): number {
    const a = this.src.cellOnScreen(c), b = this.src.cellOnScreen(new Cell(c.x + 1, c.y)), d = this.src.cellOnScreen(new Cell(c.x, c.y + 1));
    return Math.max(Math.hypot(b.x - a.x, b.y - a.y), Math.hypot(d.x - a.x, d.y - a.y));
  }
}

function unit(e: Enemy | null): { cell: Cell; enemy: number } | null { return e === null ? null : { cell: new Cell(e.cx, e.cy), enemy: e.id }; }

/** The living enemy that matches, nearest the fortress (the one the player most needs to see). */
function nearestToBase(sim: LevelSim, match: (e: Enemy) => boolean): Enemy | null {
  const base = sim.grid.base;
  let best: Enemy | null = null, bestD = Infinity;
  for (const e of sim.enemySlots) {
    if (!e.alive || !match(e)) continue;
    const d = (e.x.toNumber() - base.x) ** 2 + (e.y.toNumber() - base.y) ** 2;
    if (d < bestD) { bestD = d; best = e; }
  }
  return best;
}

/** What holds the guide back on the level screen, most important first (DESIGN §4.3). */
export interface LevelBusyInputs {
  /** HudView.busyReason: the intro card, a wave / boss / warn banner, a moment callout. */
  readonly hud: "intro" | "banner" | "callout" | null;
  readonly tooltip: boolean;
  readonly settingsOpen: boolean;
  /** The "Paused — tap to resume" layer (the guide's own hold never shows it). */
  readonly pausedLayer: boolean;
}
export function levelBusy(s: LevelBusyInputs): BusyReason | null {
  if (s.pausedLayer) return "paused";
  if (s.settingsOpen) return "sheet";
  if (s.hud !== null) return s.hud;
  return s.tooltip ? "tooltip" : null;
}

/**
 * Whether the fortress's troops are fighting: one struck an enemy within the last second of the wave. A Barracks'
 * soldiers (TOWERS_V2: troops with an owner tower) do not count — the rally hints this feeds are about the flag, which
 * moves only the fortress's troops.
 */
export class TroopFightWatch {
  static readonly RecentTicks = SimConstants.TicksPerSecond;
  private last = -Infinity;
  /** After each sim tick (its events are still in the buffer). */
  observe(sim: LevelSim): void {
    for (const ev of sim.events.all()) {
      if (ev.kind === SimEventKind.TroopAttacked && (sim.troops.find(ev.a)?.ownerTowerId ?? 0) === 0) { this.last = sim.tick; return; }
    }
  }
  fighting(sim: LevelSim): boolean { return sim.phase === LevelPhase.WaveRunning && sim.tick - this.last <= TroopFightWatch.RecentTicks; }
}

/** The level-1 walkthrough's ×1 override (DESIGN §7): the speed to force, or null to leave the player's own. */
export interface WalkthroughSpeedInputs {
  readonly level: number;
  readonly firstClear: boolean;
  /** A guide is installed (none with ?noguide: no override). */
  readonly guide: boolean;
  /** l1.speed is done (it asks the player to speed up). */
  readonly speedTaught: boolean;
  readonly walkthroughSkipped: boolean;
  /** Captain's tips off: l1.speed, a tip, will never show, so nothing would lift the override. */
  readonly tipsOff: boolean;
  /** The player tapped the speed button in this run. */
  readonly speedTapped: boolean;
}
/** The walkthrough's speed. */
export const WALKTHROUGH_SPEED = 1;
export function walkthroughSpeed(s: WalkthroughSpeedInputs): number | null {
  const on = s.guide && s.level === 1 && s.firstClear && !s.speedTaught && !s.walkthroughSkipped && !s.tipsOff && !s.speedTapped;
  return on ? WALKTHROUGH_SPEED : null;
}

/** The last step of the level-1 draft lesson (scripts/level1.ts): once it is done the tray may speak between lines. */
export const WALKTHROUGH_DRAFT_LAST = "l1.start";

/** What decides whether the card tray's own hint lines keep quiet for Captain Bram (one voice, DESIGN §6.2). */
export interface TrayQuietInputs {
  readonly level: number;
  /** The captain has a line up, or had one within TRAY_LINGER_MS (any line, not only the walkthrough's). */
  readonly captainSpeaking: boolean;
  readonly walkthroughSkipped: boolean;
  /** WALKTHROUGH_DRAFT_LAST is done. */
  readonly draftTaught: boolean;
}
/**
 * After the captain's line goes, the tray's hint stays hidden this long: two lines in a row (the step gap, the quiet
 * grace) left a moment in which the hint flashed up between them (owner 2026-09-30: "hints are flashing on/off").
 */
export const TRAY_LINGER_MS = 1500;

/**
 * The tray keeps quiet while the captain speaks (any line: one voice), a moment after, and through the whole level-1
 * draft lesson: between two lines (the step gap, a callout) the tray's hint ("Tap a card, then PLACE its road") would
 * otherwise flash up and say what the captain is about to say, so every lesson was heard twice.
 */
export function trayQuiet(s: TrayQuietInputs): boolean {
  return s.captainSpeaking || (s.level === 1 && !s.walkthroughSkipped && !s.draftTaught);
}
